// pages/PurchaseList.js - Purchase List Page

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, onValue, remove, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "../styles/main.css";

export default function PurchaseList() {
  const navigate = useNavigate();
  
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({
    totalPurchases: 0,
    totalAmount: 0,
    totalTax: 0
  });

  // Load all financial years
  useEffect(() => {
    const financialYearsRef = ref(database, "financialYears");
    onValue(financialYearsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const years = Object.keys(data).sort().reverse();
        setFinancialYears(years);
        if (years.length > 0 && !selectedYear) {
          setSelectedYear(years[0]);
        }
      }
      setLoading(false);
    });
  }, []);

  // Load purchases for selected financial year
  useEffect(() => {
    if (!selectedYear) return;

    const purchasesRef = ref(database, `financialYears/${selectedYear}/purchases`);
    onValue(purchasesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const purchaseList = Object.entries(data).map(([id, purchase]) => ({
          id,
          ...purchase
        })).sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        setPurchases(purchaseList);
        setFilteredPurchases(purchaseList);
        
        // Calculate stats
        const totalAmount = purchaseList.reduce((sum, purchase) => sum + (purchase.grandTotal || 0), 0);
        const totalTax = purchaseList.reduce((sum, purchase) => sum + (purchase.totalGST || 0), 0);
        
        setStats({
          totalPurchases: purchaseList.length,
          totalAmount,
          totalTax
        });
      } else {
        setPurchases([]);
        setFilteredPurchases([]);
        setStats({
          totalPurchases: 0,
          totalAmount: 0,
          totalTax: 0
        });
      }
    });
  }, [selectedYear]);

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPurchases(purchases);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = purchases.filter(purchase => {
      return (
        purchase.purchaseNo?.toLowerCase().includes(term) ||
        purchase.supplierName?.toLowerCase().includes(term) ||
        purchase.supplierMobile?.includes(term) ||
        purchase.supplierGST?.toLowerCase().includes(term) ||
        purchase.supplierInvoiceNo?.toLowerCase().includes(term) ||
        purchase.notes?.toLowerCase().includes(term) ||
        purchase.items?.some(item => 
          item.name?.toLowerCase().includes(term) ||
          item.hsn?.toLowerCase().includes(term)
        )
      );
    });
    
    setFilteredPurchases(filtered);
  }, [searchTerm, purchases]);

  const handleDelete = async (purchaseId) => {
    if (!selectedYear) return;
    
    if (window.confirm("Are you sure you want to delete this purchase?")) {
      try {
        const purchaseRef = ref(database, `financialYears/${selectedYear}/purchases/${purchaseId}`);
        await remove(purchaseRef);
        alert("Purchase deleted successfully");
      } catch (error) {
        console.error("Error deleting purchase:", error);
        alert("Failed to delete purchase");
      }
    }
  };

  const handleEdit = (purchaseId) => {
    navigate(`/purchase/edit/${purchaseId}`);
  };

  const handleView = (purchaseId) => {
    navigate(`/purchase/view/${purchaseId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading purchases...</p>
      </div>
    );
  }

  return (
    <div className="purchase-list-app">
      <div className="page-header">
        <h2>Purchase Orders</h2>
        <button 
          className="create-btn"
          onClick={() => navigate("/purchase/new")}
        >
          + New Purchase
        </button>
      </div>

      {/* Financial Year Selector */}
      <div className="financial-year-selector">
        <label>Financial Year:</label>
        <select 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(e.target.value)}
          className="year-select"
        >
          {financialYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by purchase no., supplier name, mobile, GST, items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={clearSearch}>
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-count">
            Found {filteredPurchases.length} {filteredPurchases.length === 1 ? 'purchase' : 'purchases'}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats.totalPurchases > 0 && (
        <div className="stats-cards">
          <div className="stat-card">
            <span className="stat-label">Total Purchases</span>
            <span className="stat-value">{stats.totalPurchases}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Amount</span>
            <span className="stat-value">{formatCurrency(stats.totalAmount)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Tax</span>
            <span className="stat-value">{formatCurrency(stats.totalTax)}</span>
          </div>
        </div>
      )}

      {/* Purchase List */}
      <div className="purchases-container">
        {filteredPurchases.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? (
              <>
                <p>No purchases found matching "{searchTerm}"</p>
                <button 
                  className="clear-search-btn"
                  onClick={clearSearch}
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <p>No purchases found for financial year {selectedYear}</p>
                <button 
                  className="create-first-btn"
                  onClick={() => navigate("/purchase/new")}
                >
                  Create First Purchase
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="purchase-cards">
            {filteredPurchases.map(purchase => (
              <div key={purchase.id} className="purchase-card">
                <div className="purchase-card-header">
                  <div className="header-left">
                    <span className="purchase-no">{purchase.purchaseNo || "No Number"}</span>
                    {purchase.supplierInvoiceNo && (
                      <span className="supplier-invoice">Supplier: {purchase.supplierInvoiceNo}</span>
                    )}
                  </div>
                  <span className="series-badge">{purchase.series || "Main"}</span>
                </div>
                
                <div className="purchase-card-body">
                  <div className="supplier-info">
                    <strong>{purchase.supplierName || "Unknown Supplier"}</strong>
                    {purchase.supplierMobile && (
                      <span className="mobile">📱 {purchase.supplierMobile}</span>
                    )}
                  </div>
                  
                  <div className="purchase-details">
                    <span>📅 {formatDate(purchase.date)}</span>
                    <span className="amount">💰 {formatCurrency(purchase.grandTotal)}</span>
                  </div>
                  
                  {purchase.items && (
                    <div className="items-count">
                      📦 {purchase.items.length} {purchase.items.length === 1 ? 'item' : 'items'}
                    </div>
                  )}

                  {/* Tax Info */}
                  {purchase.totalGST > 0 && (
                    <div className="tax-info">
                      <span className="tax-badge">GST: {formatCurrency(purchase.totalGST)}</span>
                    </div>
                  )}

                  {/* ITC Summary */}
                  {purchase.items && purchase.items.some(item => item.itc) && (
                    <div className="itc-summary">
                      <span className="itc-label">ITC:</span>
                      {purchase.items.filter(item => item.itc === "Eligible").length > 0 && (
                        <span className="itc-badge eligible">
                          ✓ Eligible: {purchase.items.filter(item => item.itc === "Eligible").length}
                        </span>
                      )}
                      {purchase.items.filter(item => item.itc === "Partially Eligible").length > 0 && (
                        <span className="itc-badge partial">
                          ⚡ Partial: {purchase.items.filter(item => item.itc === "Partially Eligible").length}
                        </span>
                      )}
                      {purchase.items.filter(item => item.itc === "Ineligible").length > 0 && (
                        <span className="itc-badge ineligible">
                          ✗ Ineligible: {purchase.items.filter(item => item.itc === "Ineligible").length}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Preview of items for search context */}
                  {searchTerm && purchase.items && (
                    <div className="item-preview">
                      {purchase.items.slice(0, 2).map((item, idx) => (
                        <span key={idx} className="item-tag">
                          {item.name}
                          {item.qty > 1 && ` (${item.qty})`}
                        </span>
                      ))}
                      {purchase.items.length > 2 && (
                        <span className="item-tag more">+{purchase.items.length - 2} more</span>
                      )}
                    </div>
                  )}

                  {/* Notes Preview */}
                  {purchase.notes && (
                    <div className="notes-preview">
                      📝 {purchase.notes.length > 60 ? purchase.notes.substring(0, 60) + '...' : purchase.notes}
                    </div>
                  )}
                </div>
                
                <div className="purchase-card-actions">
                  <button 
                    className="view-btn"
                    onClick={() => handleView(purchase.id)}
                    title="View Purchase"
                  >
                    👁️ View
                  </button>
                  <button 
                    className="edit-btn"
                    onClick={() => handleEdit(purchase.id)}
                    title="Edit Purchase"
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(purchase.id)}
                    title="Delete Purchase"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

     {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>

      <style>{`
        .purchase-list-app {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .create-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }

        .financial-year-selector {
          padding: 15px 20px;
          background: white;
          margin: 10px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 15px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .financial-year-selector label {
          font-weight: 500;
          color: #666;
        }

        .year-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        /* Search Styles */
        .search-container {
          padding: 0 20px;
          margin-bottom: 20px;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          overflow: hidden;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          color: #999;
          font-size: 16px;
        }

        .search-input {
          flex: 1;
          padding: 14px 14px 14px 40px;
          border: 1px solid #ddd;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .search-input:focus {
          border-color: #5a60ea;
        }

        .clear-search {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #999;
          font-size: 16px;
          cursor: pointer;
          padding: 5px;
        }

        .clear-search:hover {
          color: #ff4444;
        }

        .search-results-count {
          margin-top: 8px;
          font-size: 13px;
          color: #666;
          text-align: right;
        }

        .clear-search-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          margin-top: 15px;
          cursor: pointer;
        }

        /* Stats Cards */
        .stats-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 0 20px;
          margin-bottom: 20px;
        }

        .stat-card {
          background: white;
          padding: 15px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          text-align: center;
        }

        .stat-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }

        .stat-value {
          display: block;
          font-size: 18px;
          font-weight: bold;
          color: #5a60ea;
        }

        .purchases-container {
          padding: 0 20px;
        }

        .empty-state {
          background: white;
          border-radius: 12px;
          padding: 40px 20px;
          text-align: center;
          color: #666;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .create-first-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          margin-top: 15px;
          cursor: pointer;
        }

        .purchase-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 15px;
        }

        @media (min-width: 768px) {
          .purchase-cards {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .purchase-cards {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .purchase-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }

        .purchase-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .purchase-card-header {
          background: #f8f9fa;
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .purchase-no {
          font-weight: bold;
          color: #5a60ea;
          font-size: 14px;
        }

        .supplier-invoice {
          font-size: 11px;
          color: #666;
        }

        .series-badge {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 12px;
          background: #e0e0e0;
          color: #666;
        }

        .purchase-card-body {
          padding: 15px;
        }

        .supplier-info {
          margin-bottom: 10px;
        }

        .supplier-info strong {
          display: block;
          font-size: 16px;
          margin-bottom: 3px;
        }

        .mobile {
          font-size: 12px;
          color: #666;
        }

        .purchase-details {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
        }

        .amount {
          font-weight: bold;
          color: #5a60ea;
        }

        .items-count {
          font-size: 12px;
          color: #888;
          border-top: 1px dashed #eee;
          padding-top: 8px;
          margin-bottom: 8px;
        }

        .tax-info {
          margin-bottom: 8px;
        }

        .tax-badge {
          background: #e8f0fe;
          color: #5a60ea;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
        }

        .itc-summary {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #eee;
        }

        .itc-label {
          font-size: 11px;
          color: #666;
        }

        .itc-badge {
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 10px;
        }

        .itc-badge.eligible {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .itc-badge.partial {
          background: #fff3e0;
          color: #f57c00;
        }

        .itc-badge.ineligible {
          background: #ffebee;
          color: #d32f2f;
        }

        /* Item Preview Styles */
        .item-preview {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #eee;
        }

        .item-tag {
          background: #f0f0f0;
          color: #666;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
        }

        .item-tag.more {
          background: #e3f2fd;
          color: #1976d2;
        }

        /* Notes Preview */
        .notes-preview {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #eee;
          font-size: 11px;
          color: #888;
          font-style: italic;
        }

        /* Action Buttons */
        .purchase-card-actions {
          display: flex;
          border-top: 1px solid #eee;
        }

        .purchase-card-actions button {
          flex: 1;
          padding: 10px;
          border: none;
          background: white;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }

        .purchase-card-actions button:hover {
          background: #f5f5f5;
        }

        .view-btn {
          color: #5a60ea;
        }

        .edit-btn {
          color: #ff9800;
          border-left: 1px solid #eee;
          border-right: 1px solid #eee;
        }

        .delete-btn {
          color: #f44336;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #f5f5f5;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top-color: #5a60ea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }

        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}